import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, clearLegacyTokens } from "@/lib/api";
import { clearWorkspaceDisplay } from "@/lib/workspaceDisplayCache";

const AuthCtx = createContext(null);
const OFFLINE_IDENTITY = "ck_offline_identity";

function rememberIdentity(user) {
  // Display/scope metadata only. This grants no server authorization.
  localStorage.setItem(OFFLINE_IDENTITY, JSON.stringify(user));
  return user;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [error, setError] = useState("");
  const prefetchedRef = useRef(false);

  // Perf: quietly warm up the route chunks the user lands on, once per session.
  // Fails silently, runs when idle, and never blocks login/dashboard.
  useEffect(() => {
    if (prefetchedRef.current || !user || !user.id) return;
    prefetchedRef.current = true;
    const CASHIER_ROLES = ["cashier", "staff", "outlet", "outlet_manager"];
    const warm = () => {
      try {
        if (CASHIER_ROLES.includes(user.role)) {
          import("@/pages/POS").catch(() => {});
          import("@/pages/AllOrders").catch(() => {});
          import("@/pages/OutletDayEnd").catch(() => {});
        } else if (user.role === "admin" || user.role === "manager") {
          import("@/pages/Dashboard").catch(() => {});
        }
      } catch (_) { /* prefetch is best-effort */ }
    };
    const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
    const handle = ric(warm);
    return () => {
      if (window.cancelIdleCallback && typeof handle === "number") {
        try { window.cancelIdleCallback(handle); } catch (_) { /* noop */ }
      }
    };
  }, [user]);

  useEffect(() => {
    let mounted = true;
    api
      .get("/auth/me")
      .then(({ data }) => mounted && setUser(rememberIdentity(data)))
      .catch((error) => {
        if (!mounted) return;
        if (!error.response) {
          try {
            const cached = JSON.parse(localStorage.getItem(OFFLINE_IDENTITY));
            if (cached?.id) { setUser({ ...cached, offline: true }); return; }
          } catch (_) { /* no usable offline identity */ }
        }
        localStorage.removeItem(OFFLINE_IDENTITY);
        setUser(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      await api.post("/auth/login", { email, password });
      const { data } = await api.get("/auth/me");
      setUser(rememberIdentity(data));
      return { ok: true, role: data.role };
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return { ok: false };
    }
  }, []);

  const billerLogin = useCallback(async (identifier, passcode) => {
    await api.post("/auth/biller/login", { identifier, passcode });
    const { data } = await api.get("/auth/me");
    setUser(rememberIdentity(data));
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      setError("Logout could not reach the server. Reconnect to end this session securely.");
      return false;
    }
    clearLegacyTokens();
    await clearWorkspaceDisplay(user?.id).catch(() => {});
    localStorage.removeItem(OFFLINE_IDENTITY);
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.filter((key) => key.startsWith("ssv-pos-api")).map((key) => caches.delete(key)));
    }
    setUser(false);
    return true;
  }, [user]);

  const value = useMemo(() => ({ user, error, login, billerLogin, logout, setError }), [user, error, login, billerLogin, logout]);

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
